<template>
    <div class="space-y-6">
        <!-- Celery Worker 监控 -->
        <div class="card bg-base-100 shadow-lg border border-base-200">
            <div class="card-body">
                <div class="flex justify-between items-center mb-4">
                    <h2 class="card-title text-lg font-semibold flex items-center">
                        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"></path>
                        </svg>
                        {{ $tt('worker.monitor.title') }}
                    </h2>
                    <div class="flex items-center gap-2">
                        <div class="flex items-center gap-1">
                            <div class="w-2 h-2 rounded-full" :class="isConnected ? 'bg-success animate-pulse' : 'bg-error'"></div>
                            <span class="text-sm" :class="isConnected ? 'text-success' : 'text-error'">
                                {{ isConnected ? $tt('worker.monitor.connection.connected') : $tt('worker.monitor.connection.failed') }}
                            </span>
                        </div>
                        <button 
                            class="btn btn-sm btn-outline btn-primary" 
                            @click="refreshWorkerStatus"
                            :disabled="isLoading"
                        >
                            <svg v-if="isLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                            </svg>
                            <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                            </svg>
                            {{ $tt('刷新') }}
                        </button>
                    </div>
                </div>

                <div class="mb-4 p-3 bg-info/10 rounded-lg border border-info/20">
                    <div class="text-sm text-base-content/80">
                        {{ $tt('worker.monitor.desc') }}
                    </div>
                </div>

                <!-- 统计概览 -->
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div class="stat bg-base-200/50 rounded-lg">
                        <div class="stat-figure text-primary">
                            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                            </svg>
                        </div>
                        <div class="stat-title">{{ $tt('Worker 节点') }}</div>
                        <div class="stat-value text-primary">{{ workerCount }}</div>
                        <div class="stat-desc">{{ $tt('在线节点数量') }}</div>
                    </div>

                    <div class="stat bg-base-200/50 rounded-lg">
                        <div class="stat-figure text-secondary">
                            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                        </div>
                        <div class="stat-title">{{ $tt('活跃任务') }}</div>
                        <div class="stat-value text-secondary">{{ activeTaskCount }}</div>
                        <div class="stat-desc">{{ $tt('正在执行中') }}</div>
                    </div>

                    <div class="stat bg-base-200/50 rounded-lg">
                        <div class="stat-figure text-accent">
                            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                        </div>
                        <div class="stat-title">{{ $tt('预留任务') }}</div>
                        <div class="stat-value text-accent">{{ reservedTaskCount }}</div>
                        <div class="stat-desc">{{ $tt('队列中等待') }}</div>
                    </div>
                </div>

                <!-- Worker 节点列表 -->
                <div class="space-y-4">
                    <h3 class="text-lg font-medium">{{ $tt('Worker 节点状态') }}</h3>
                    <div v-if="workerData && Object.keys(workerData).length > 0" class="space-y-3">
                        <div v-for="(worker, workerName) in workerData" :key="workerName" class="card bg-base-200/30 border border-base-200">
                            <div class="card-body p-4">
                                <div class="flex justify-between items-start mb-3">
                                    <div>
                                        <h4 class="font-medium text-base">{{ workerName }}</h4>
                                        <div class="flex items-center gap-2 mt-1">
                                            <div class="badge badge-success badge-sm">{{ $tt('在线') }}</div>
                                            <span class="text-sm text-base-content/70">
                                                {{ $tt('worker.monitor.activeCount', { count: worker ? worker.length : 0 }) }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="text-right">
                                        <div class="text-sm text-base-content/70">
                                            {{ $tt('worker.monitor.lastUpdated') }} {{ formatDate(new Date()) }}
                                        </div>
                                    </div>
                                </div>

                                <!-- 活跃任务列表 -->
                                <div v-if="worker && worker.length > 0" class="mt-4">
                                    <h5 class="font-medium text-sm mb-2 flex items-center">
                                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        {{ $tt('worker.monitor.activeTasksTitle', { count: worker.length }) }}
                                    </h5>
                                    <div class="space-y-3">
                                        <div v-for="(task, index) in worker" :key="index" class="bg-base-100 rounded-lg p-4 border border-base-300 hover:border-base-400 transition-colors">
                                            <div class="flex justify-between items-start gap-4">
                                                <div class="flex-1 min-w-0">
                                                    <!-- 任务名称 -->
                                                    <div class="font-medium text-sm mb-2 flex items-center gap-2">
                                                        <span class="text-primary">{{ getTaskDisplayName(task.name) }}</span>
                                                        <div class="badge badge-primary badge-sm">{{ $tt('执行中') }}</div>
                                                    </div>
                                                    
                                                    <!-- 任务ID -->
                                                    <div class="text-xs text-base-content/60 mb-2 font-mono bg-base-200 px-2 py-1 rounded">
                                                        <span class="font-medium">{{ $tt('worker.monitor.idLabel') }}</span>
                                                        <span class="select-all">{{ task.id }}</span>
                                                        <button 
                                                            @click="copyTaskId(task.id)" 
                                                            class="ml-2 hover:text-primary cursor-pointer"
                                                            :title="$tt('复制任务ID')"
                                                        >
                                                            <svg class="w-3 h-3 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                                            </svg>
                                                        </button>
                                                    </div>
                                                    
                                                    
                                                    <!-- 运行时长 -->
                                                    <div v-if="task.time_start" class="text-xs text-base-content/60">
                                                        <span class="font-medium">{{ $tt('开始时间:') }}</span> {{ formatTimestamp(task.time_start) }}
                                                        <span class="ml-3 font-medium">{{ $tt('运行时长:') }}</span> {{ getRunningDuration(task.time_start) }}
                                                    </div>
                                                </div>
                                                
                                                <!-- 操作按钮 -->
                                                <div class="flex flex-col gap-2">
                                                    <button 
                                                        @click="stopTask(task.id)"
                                                        class="btn btn-sm btn-error btn-outline"
                                                        :disabled="isStoppingTask === task.id"
                                                        :title="$tt('停止任务')"
                                                    >
                                                        <svg v-if="isStoppingTask === task.id" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                                                        </svg>
                                                        <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"></path>
                                                        </svg>
                                                        {{ isStoppingTask === task.id ? $tt('worker.monitor.stopping') : $tt('停止') }}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- 预留任务列表 -->
                                <div v-if="reservedData[workerName] && reservedData[workerName].length > 0" class="mt-4">
                                    <h5 class="font-medium text-sm mb-2 flex items-center">
                                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        {{ $tt('worker.monitor.reservedTasksTitle', { count: reservedData[workerName].length }) }}
                                    </h5>
                                    <div class="space-y-2">
                                        <div v-for="(task, index) in reservedData[workerName]" :key="index" class="bg-base-100 rounded-lg p-3 border border-base-300">
                                            <div class="flex justify-between items-start">
                                                <div class="flex-1">
                                                    <div class="font-medium text-sm">{{ task.name || $tt('worker.monitor.unknownTask') }}</div>
                                                    <div class="text-xs text-base-content/60 mt-1">
                                                        {{ $tt('worker.monitor.taskIdLabel') }} {{ task.id }}
                                                    </div>
                                                </div>
                                                <div class="badge badge-warning badge-sm">{{ $tt('等待中') }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- 无任务状态 -->
                                <div v-if="(!worker || worker.length === 0) && (!reservedData[workerName] || reservedData[workerName].length === 0)" class="text-center py-4">
                                    <svg class="w-12 h-12 mx-auto text-base-content/30 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                    <div class="text-sm text-base-content/60">{{ $tt('该 Worker 当前无任务') }}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 无 Worker 状态 -->
                    <div v-else class="text-center py-8">
                        <svg class="w-16 h-16 mx-auto text-base-content/30 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
                        </svg>
                        <div class="text-lg font-medium text-base-content/60 mb-2">{{ $tt('暂无 Worker 节点') }}</div>
                        <div class="text-sm text-base-content/50">{{ $tt('请确保 Celery Worker 已启动') }}</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'WorkerMonitor',
    data() {
        return {
            workerData: {},
            reservedData: {},
            isLoading: false,
            isConnected: false,
            refreshTimer: null,
            isStoppingTask: null // 当前正在停止的任务ID
        }
    },
    computed: {
        workerCount() {
            return Object.keys(this.workerData).length
        },
        activeTaskCount() {
            let count = 0
            for (const worker of Object.values(this.workerData)) {
                if (worker) {
                    count += worker.length
                }
            }
            return count
        },
        reservedTaskCount() {
            let count = 0
            for (const tasks of Object.values(this.reservedData)) {
                if (tasks) {
                    count += tasks.length
                }
            }
            return count
        }
    },
    mounted() {
        // 移除自动启动，等待外部调用
    },
    beforeDestroy() {
        this.stopAutoRefresh()
    },
    methods: {
        async loadWorkerStatus() {
            this.isLoading = true
            try {
                const response = await this.$api.Task.activeQueue()
                if (response.result) {
                    this.workerData = response.data.active_tasks || {}
                    this.reservedData = response.data.reserved_tasks || {}
                    this.isConnected = true
                } else {
                    this.isConnected = false
                    this.$myMsg.notify({
                        title: this.$tt('获取 Worker 状态失败'),
                        message: response.message || this.$tt('worker.monitor.checkCelery'),
                        type: 'error',
                        time: 3000
                    })
                }
            } catch (error) {
                this.isConnected = false
                console.error('获取 Worker 状态错误:', error)
                this.$myMsg.notify({
                    title: this.$tt('网络错误'),
                    message: this.$tt('无法获取 Worker 状态信息'),
                    type: 'error',
                    time: 3000
                })
            } finally {
                this.isLoading = false
            }
        },
        refreshWorkerStatus() {
            this.loadWorkerStatus()
        },
        startAutoRefresh() {
            this.refreshTimer = setInterval(() => {
                this.loadWorkerStatus()
            }, 5000) // 每5秒刷新一次
        },
        stopAutoRefresh() {
            if (this.refreshTimer) {
                clearInterval(this.refreshTimer)
                this.refreshTimer = null
            }
        },
        formatTimestamp(timestamp) {
            if (!timestamp) return this.$tt('worker.monitor.unknown')
            const date = new Date(timestamp * 1000)
            const locale = localStorage.getItem('mtw_app_locale') || 'zh-CN'
            return date.toLocaleTimeString(locale)
        },
        formatDate(date) {
            const locale = localStorage.getItem('mtw_app_locale') || 'zh-CN'
            return date.toLocaleString(locale)
        },
        // 启动监控
        startMonitoring() {
            this.loadWorkerStatus()
            this.startAutoRefresh()
        },
        // 停止监控
        stopMonitoring() {
            this.stopAutoRefresh()
        },
        // 获取任务显示名称
        getTaskDisplayName(taskName) {
            const taskNameMap = {
                // 扫描任务
                'applications.task.tasks.full_scan_folder': '全量扫描文件夹',
                'applications.task.tasks.update_scan_folder': '增量扫描文件夹',
                'applications.task.tasks.scan_folder': '扫描文件夹',
                'applications.task.tasks.scan_music_id3': '扫描音乐ID3',
                'applications.task.tasks.scan': '完整扫描',
                'applications.task.tasks.full_scan_folder_simple': '简化全量扫描',

                // 元数据处理任务
                'applications.task.tasks.manual_tag_task': '手动修改元数据',
                'applications.task.tasks.batch_auto_tag_task': '批量自动刮削',
                'applications.task.tasks.batch_auto_tag_task_v2': '批量自动刮削V2',
                'applications.task.tasks.missing_task': '补充缺失元数据',
                'applications.task.tasks.rule_tag_task': '规则刮削风格',
                'applications.task.tasks.auto_tag_task': '自动刮削任务(废弃)',
                'applications.task.tasks.match_song_celery': '歌曲匹配',
                'applications.task.tasks.mathc_song_and_move': '匹配歌曲',

                // 导入任务
                'applications.task.tasks.import_music_task': '导入音乐收藏',
                'applications.task.tasks.save_music_task': '保存音乐元数据',
                'applications.task.tasks.save_music_task_batch': '批量保存音乐元数据',
                'applications.task.tasks.save_music_in_db': '保存音乐到数据库',

                // 文件处理任务
                'applications.task.tasks.tidy_folder_task': '整理文件夹',
                'applications.task.tasks.remove_null_folder_task': '删除空文件夹',
                'applications.task.tasks.format_music_type_task': '音频格式转换',
                'applications.task.tasks.split_cue': '切割CUE音轨',

                // 系统维护任务
                'applications.task.tasks.backend_track_task': '删除无效音乐记录',
                'applications.task.tasks.backend_artist_task': '后台刮削艺术家',
                'applications.task.tasks.backend_stop_task': '停止后台任务',
                'applications.task.tasks.backend_music': '清理音乐数据',
                'applications.task.tasks.backend_audit_log': '清理审计日志',
                'applications.task.tasks.backend_play_log': '清理播放日志',
                'applications.task.tasks.backend_tag_log': '清理标签日志',

                // 重复检查任务
                'applications.task.tasks.check_for_duplicates_task': '重复文件检查',

                // 专辑艺术家任务
                'applications.task.tasks.task_fix_all_artist_issues': '修复所有艺术家问题',
                'applications.task.tasks.rewrite_metadata_album': '重写专辑元数据',
                'applications.task.tasks.rewrite_metadata_artist': '重写艺术家元数据',

                // 下载相关任务
                'applications.task.tasks.batch_auto_tag_task_from_download': '从下载目录刮削',

                // 工作器任务
                'applications.task.tasks.scrape_worker': '刮削工作器',
                'applications.task.tasks.sqlite_serial_processor': 'SQLite串行处理器',

                // 小米音箱任务
                'applications.task.tasks.play_xiaomi_queue': '小米音箱播放',

                // 其他任务
                'applications.task.tasks.generate_strm_task': '生成STRM文件',
                'applications.task.tasks.delete_metadata': '删除元数据',
                'applications.task.tasks.test_add_sleep': '测试睡眠任务',
                'applications.task.tasks.ping_pong': '心跳检测',
                'applications.task.tasks.get_decode_music_path_celery': '解码音乐路径'
            }
            return this.$tt(taskNameMap[taskName] || taskName || 'worker.monitor.unknownTask')
        },
       
        // 获取运行时长
        getRunningDuration(startTime) {
            if (!startTime) return this.$tt('worker.monitor.unknown')
            const now = Date.now() / 1000
            const duration = Math.floor(now - startTime)
            
            if (duration < 60) {
                return this.$tt('worker.monitor.duration.seconds', { duration })
            } else if (duration < 3600) {
                const minutes = Math.floor(duration / 60)
                const seconds = duration % 60
                return this.$tt('worker.monitor.duration.minutesSeconds', { minutes, seconds })
            } else {
                const hours = Math.floor(duration / 3600)
                const minutes = Math.floor((duration % 3600) / 60)
                return this.$tt('worker.monitor.duration.hoursMinutes', { hours, minutes })
            }
        },
        // 复制任务ID
        copyTaskId(taskId) {
            if (navigator.clipboard) {
                navigator.clipboard.writeText(taskId).then(() => {
                    this.$myMsg.notify({
                        title: this.$tt('复制成功'),
                        message: this.$tt('任务ID已复制到剪贴板'),
                        type: 'success',
                        time: 2000
                    })
                }).catch(() => {
                    this.fallbackCopy(taskId)
                })
            } else {
                this.fallbackCopy(taskId)
            }
        },
        // 降级复制方法
        fallbackCopy(text) {
            const textArea = document.createElement('textarea')
            textArea.value = text
            document.body.appendChild(textArea)
            textArea.select()
            try {
                document.execCommand('copy')
                this.$myMsg.notify({
                    title: this.$tt('复制成功'),
                    message: this.$tt('任务ID已复制到剪贴板'),
                    type: 'success',
                    time: 2000
                })
            } catch (err) {
                this.$myMsg.notify({
                    title: this.$tt('复制失败'),
                    message: this.$tt('请手动复制任务ID'),
                    type: 'error',
                    time: 3000
                })
            }
            document.body.removeChild(textArea)
        },
        // 停止指定任务
        async stopTask(taskId) {
            this.isStoppingTask = taskId
            try {
                const response = await this.$api.Task.task1({ task_id: taskId })
                if (response.result) {
                    this.$myMsg.notify({
                        title: this.$tt('任务停止成功'),
                        message: response.message || this.$tt('worker.monitor.taskStopped', { taskId }),
                        type: 'success',
                        time: 3000
                    })
                    // 立即刷新状态
                    this.loadWorkerStatus()
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('停止任务失败'),
                        message: response.message || this.$tt('worker.monitor.stopError'),
                        type: 'error',
                        time: 3000
                    })
                }
            } catch (error) {
                console.error('停止任务错误:', error)
                this.$myMsg.notify({
                    title: this.$tt('网络错误'),
                    message: this.$tt('无法停止任务，请检查网络连接'),
                    type: 'error',
                    time: 3000
                })
            } finally {
                this.isStoppingTask = null
            }
        }
    }
}
</script>
